import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PATCH_DEBOUNCE_MS = 800;

function loadSet(key) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    return new Set(Array.isArray(arr) ? arr : []);
  } catch {
    return new Set();
  }
}

function saveSet(key, set) {
  try {
    window.localStorage.setItem(key, JSON.stringify(Array.from(set)));
  } catch {
    // no-op (private mode / quota)
  }
}

/**
 * useDismissibleFeed — tracks viewed + dismissed IDs for a top-bar feed
 * (Broadcasts / Activity / Login-page bell).
 *
 * Storage strategy:
 *   - localStorage is the offline-first cache (always written, instant UX)
 *   - Optionally, when `serverSync` is enabled, the hook also:
 *       * one-time GETs the server state on mount, union-merges it into local
 *       * debounce-PATCHes deltas to the server so dismissals follow the
 *         user across devices
 *   The server endpoint is fixed: GET/PATCH `/api/notifications/state`.
 *
 * Args:
 *   items       — current visible feed payload from the API
 *   storageKey  — localStorage namespace (e.g. 'broadcasts')
 *   getId       — derive a stable string id per item
 *   options     — { serverSync?: boolean, feedKey?: 'activity'|'broadcasts' }
 *                  serverSync requires a feedKey matching the backend.
 */
export function useDismissibleFeed(
  items,
  storageKey,
  getId = (it, i) => it?.id || String(i),
  options = {},
) {
  const { serverSync = false, feedKey } = options;
  const viewedKey = `topbar.${storageKey}.viewed`;
  const dismissedKey = `topbar.${storageKey}.dismissed`;

  const [viewed, setViewed] = useState(() => loadSet(viewedKey));
  const [dismissed, setDismissed] = useState(() => loadSet(dismissedKey));

  // Persist to localStorage on every mutation. Kept out of the click
  // handlers so they stay O(1).
  useEffect(() => { saveSet(viewedKey, viewed); }, [viewed, viewedKey]);
  useEffect(() => { saveSet(dismissedKey, dismissed); }, [dismissed, dismissedKey]);

  // ---- One-time server hydration (multi-device cross-fade) ----
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (!serverSync || !feedKey || hydratedRef.current) return;
    // Skip when there's no auth token yet — AuthProvider hydrates it
    // from localStorage on mount but this hook may run first on some
    // pages. A pre-auth GET here produces a harmless-but-noisy 403 on
    // first paint. We retry on the next hook mount (route change or
    // login-triggered remount) once the token is present.
    if (typeof window !== 'undefined' && !window.localStorage.getItem('token')) {
      return;
    }
    hydratedRef.current = true;
    let cancelled = false;
    axios
      .get(`${BACKEND_URL}/api/notifications/state`)
      .then((r) => {
        if (cancelled) return;
        const sub = r.data?.[feedKey] || {};
        const remoteViewed = Array.isArray(sub.viewed_ids) ? sub.viewed_ids : [];
        const remoteDismissed = Array.isArray(sub.dismissed_ids) ? sub.dismissed_ids : [];
        if (remoteViewed.length) {
          setViewed((prev) => {
            const next = new Set(prev);
            for (const id of remoteViewed) next.add(id);
            return next.size === prev.size ? prev : next;
          });
        }
        if (remoteDismissed.length) {
          setDismissed((prev) => {
            const next = new Set(prev);
            for (const id of remoteDismissed) next.add(id);
            return next.size === prev.size ? prev : next;
          });
        }
      })
      .catch(() => { /* offline / unauth → fall back to localStorage only */ });
    return () => { cancelled = true; };
  }, [serverSync, feedKey]);

  // ---- Debounced PATCH of in-session deltas ----
  // We track *what we've already shipped* so the second PATCH only
  // carries the new ids. Keeps requests tiny + idempotent.
  const shippedViewedRef = useRef(new Set());
  const shippedDismissedRef = useRef(new Set());
  const patchTimerRef = useRef(null);
  useEffect(() => {
    if (!serverSync || !feedKey) return;
    if (patchTimerRef.current) clearTimeout(patchTimerRef.current);
    patchTimerRef.current = setTimeout(() => {
      const newViewed = [];
      for (const id of viewed) {
        if (!shippedViewedRef.current.has(id)) newViewed.push(id);
      }
      const newDismissed = [];
      for (const id of dismissed) {
        if (!shippedDismissedRef.current.has(id)) newDismissed.push(id);
      }
      if (newViewed.length === 0 && newDismissed.length === 0) return;
      const payload = {};
      payload[`${feedKey}_viewed_ids`] = newViewed;
      payload[`${feedKey}_dismissed_ids`] = newDismissed;
      axios
        .patch(`${BACKEND_URL}/api/notifications/state`, payload)
        .then(() => {
          for (const id of newViewed) shippedViewedRef.current.add(id);
          for (const id of newDismissed) shippedDismissedRef.current.add(id);
        })
        .catch(() => { /* swallow — next change will retry */ });
    }, PATCH_DEBOUNCE_MS);
    return () => { if (patchTimerRef.current) clearTimeout(patchTimerRef.current); };
  }, [viewed, dismissed, serverSync, feedKey]);

  const tagged = useMemo(
    () => (items || []).map((it, idx) => ({ id: getId(it, idx), it })),
    [items, getId],
  );

  const visibleItems = useMemo(
    () => tagged.filter(({ id }) => !dismissed.has(id)).map(({ it }) => it),
    [tagged, dismissed],
  );

  const unreadCount = useMemo(
    () => tagged.reduce((n, { id }) => (
      !dismissed.has(id) && !viewed.has(id) ? n + 1 : n
    ), 0),
    [tagged, dismissed, viewed],
  );

  const markAllAsViewed = useCallback(() => {
    setViewed((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const { id } of tagged) {
        if (!next.has(id)) { next.add(id); changed = true; }
      }
      return changed ? next : prev;
    });
  }, [tagged]);

  const dismissOne = useCallback((id) => {
    setDismissed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
    setViewed((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  return { visibleItems, unreadCount, markAllAsViewed, dismissOne };
}

export default useDismissibleFeed;
